import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import headerData from '../data/headerData.json';
import './Header.css';

const Header = ({ isNotFound }) => { 
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isFeatureActive, setIsFeatureActive] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Header shadow on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ✅ Reset feature active when leaving home
  useEffect(() => {
    if (location.pathname !== '/') {
      setIsFeatureActive(false);
    }
  }, [location.pathname]);

  // ✅ Scroll Spy for Features
  useEffect(() => {
    if (location.pathname !== '/') return;

    let observer;

    const setupObserver = () => {
      const el = document.getElementById('features');

      if (!el) {
        setTimeout(setupObserver, 200);
        return;
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setIsFeatureActive(entry.isIntersecting);
        },
        {
          root: null,
          rootMargin: '-25% 0px -55% 0px',
          threshold: 0.1,
        }
      );

      observer.observe(el);
    };

    setupObserver();

    return () => {
      if (observer) observer.disconnect();
    };
  }, [location.pathname]);

  const handleMenuToggle = () => {
    setIsMenuOpen(!isMenuOpen);
    document.body.classList.toggle('open');
  };

  // ✅ Handle route + hash scroll
  useEffect(() => {
    setIsMenuOpen(false);
    document.body.classList.remove('open');

    if (location.pathname === '/' && location.hash) {
      setTimeout(() => {
        const targetId = location.hash.substring(1);
        const targetElement = document.getElementById(targetId);

        if (targetElement) {
          const headerHeight =
            document.querySelector('header')?.offsetHeight || 0;

          const targetPosition = targetElement.offsetTop - headerHeight;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });
        }
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  };

  const handleCustomLink = (e, link) => {
    if (!link.includes('#')) return;

    e.preventDefault();
    const targetId = link.split('#')[1];

    if (location.pathname === '/') {
      const targetElement = document.getElementById(targetId);
      if (targetElement) {
        const headerHeight =
          document.querySelector('header')?.offsetHeight || 0;

        const targetPosition = targetElement.offsetTop - headerHeight;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth',
        });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const targetElement = document.getElementById(targetId);
        if (targetElement) {
          const headerHeight =
            document.querySelector('header')?.offsetHeight || 0;

          const targetPosition = targetElement.offsetTop - headerHeight;

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth',
          });
        }
      }, 400);
    }
  };

  // ✅ Include 404 condition
  const headerVersion =
    isNotFound || // 🔥 ADD THIS
    location.pathname === '/about' ||
    location.pathname === '/terms' ||
    location.pathname === '/privacy' ||
    location.pathname === '/cookie-policy' ||
    location.pathname === '/consumer-health-data-privacy-policy' ||
    location.pathname === '/safety-policy-center' ||
    location.pathname === '/dating-safety-tips' ||
    location.pathname === '/community-guidelines' ||
    location.pathname === '/california-consumer-privacy-act' ||
    location.pathname === '/faq'
      ? 'version3'
      : '';

  const pageName =
    location.pathname === '/'
      ? 'home'
      : location.pathname.replace(/^\//, '').replace(/\//g, '-');

  return (
    <header
      className={`header_wrappper version1 ${headerVersion} ${
        isScrolled ? 'scrolled' : ''
      } ${isNotFound ? 'header-404' : ''} page-${pageName}`} // ✅ ADD CLASS HERE
      id="header"
    >
      <div className="wrap">
        <div className="container null">
          {/* Logo */}
          <Link
            to={headerData.logoLink}
            className="main_logo icon"
            onClick={handleLogoClick}
          >
            <img src={headerData.logo} alt="Peck Dating" />
          </Link>

          {/* Navigation */}
          <nav>
            {/* Hamburger */}
            <div
              id="hamburger"
              className={`hamburger ${isMenuOpen ? 'active' : ''}`}
              onClick={handleMenuToggle}
            >
              <div
                className={`menu-btn-1 ${
                  isMenuOpen ? 'active' : 'closed'
                }`}
              >
                <span></span>
              </div>
            </div>

            {/* Menu */}
            <div
              className={`custom_collapse ${
                isMenuOpen ? 'open' : 'closed'
              }`}
            >
              <ul>
                {headerData.menuItems.map((item, index) => {
                  const isFeatureLink =
                    item.link.includes('#features');

                  const itemPath = item.link.split('#')[0];

                  const isRouteActive =
                    !isFeatureLink &&
                    ((itemPath === '/' && location.pathname === '/') ||
                      (itemPath !== '/' &&
                        location.pathname === itemPath));

                  return (
                    <li
                      key={index}
                      className={isRouteActive ? 'active' : ''}
                    >
                      {item.isExternal ? (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {item.text}
                        </a>
                      ) : item.isCustomLink ? (
                        <a
                          href={item.link}
                          className={`features_link custom_link ${
                            isFeatureLink && isFeatureActive
                              ? 'active'
                              : ''
                          }`}
                          onClick={(e) =>
                            handleCustomLink(e, item.link)
                          }
                        >
                          {item.text}
                        </a>
                      ) : (
                        <Link to={item.link}>{item.text}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;